import React from 'react';
import { Card, Typography, Row, Col, Progress } from 'antd';
import PropTypes from 'prop-types';
import moment from 'moment';
const { Title, Text } = Typography;

const formatLastPingTime = (value) => {
	if (!value || value === '-' || value === 'null') return '-';

	if (typeof value === 'string' && /^\d{2}:\d{2}:\d{2}$/.test(value)) {
		const parsedTime = moment.utc(value, 'HH:mm:ss', true);
		return parsedTime.isValid()
			? parsedTime.add(5, 'hours').add(30, 'minutes').format('HH:mm:ss')
			: '-';
	}

	const normalizedValue =
		typeof value === 'string' && !/[zZ]|[+-]\d{2}:\d{2}$/.test(value)
			? `${value.replace(' ', 'T')}Z`
			: value;

	const parsedTime = moment.utc(normalizedValue, moment.ISO_8601, true);
	return parsedTime.isValid()
		? parsedTime.add(5, 'hours').add(30, 'minutes').format('HH:mm:ss')
		: '-';
};

const formatKm = (value) => {
	if (value == null || value === '-' || value === '') return '-';
	return `${value} km`;
};

function DistanceMetricsCard({ distance, progress }) {
	const finalDistance = distance?.final_km;
	const finalSource = distance?.final_source;
	const actualDistanceLabel = `Actual Distance Covered ${finalSource ? `(${finalSource})` : ''}`;
	const actualDistanceValue = finalDistance;

	return (
		<Card
			bodyStyle={{ padding: '0px' }}
			style={{
				borderRadius: '12px',
				marginBottom: '16px',
				border: 'none',
				boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
			}}
		>
			<div style={{ padding: '20px 24px 8px 24px' }}>
				<Title level={4} style={{ margin: 0, fontWeight: 700, color: '#1f1f1f' }}>
					Distance Metrics
				</Title>
			</div>

			<div style={{ padding: '16px 24px 24px 24px' }}>
				<div
					style={{
						backgroundColor: '#f8f9fa',
						borderRadius: '8px',
						padding: '20px 24px',
						borderLeft: '4px solid #3b82f6',
						marginBottom: '20px',
					}}
				>
					<Text
						strong
						style={{ fontSize: '12px', color: '#3b82f6', letterSpacing: '0.8px' }}
					>
						GPS DISTANCE
					</Text>
					<Row justify="space-between" align="middle" style={{ marginTop: '16px' }}>
						<Col>
							<Text type="secondary" style={{ fontSize: '14px', color: '#64748b' }}>
								Planned Distance
							</Text>
						</Col>
						<Col>
							<Text strong>
								{distance?.planned_distance_km != null
									? `${distance.planned_distance_km} km`
									: '-'}
							</Text>
						</Col>
					</Row>
					<Row justify="space-between" align="middle" style={{ marginTop: '16px' }}>
						<Col>
							<Text type="secondary" style={{ fontSize: '14px', color: '#64748b' }}>
								{actualDistanceLabel}
							</Text>
						</Col>
						<Col>
							<Text strong style={{ fontSize: '15px', color: '#1e293b' }}>
								{actualDistanceValue != null ? `${actualDistanceValue} km` : '-'}
							</Text>
						</Col>
					</Row>
					<Row justify="space-between" align="middle" style={{ marginTop: '16px' }}>
						<Col>
							<Text type="secondary" style={{ fontSize: '14px', color: '#64748b' }}>
								Current GPS Status
							</Text>
						</Col>
						<Col>
							<Text
								strong
								style={{
									padding: '2px 8px',
									borderRadius: '4px',
									backgroundColor:
										distance?.gps_status?.toLowerCase() === 'offline'
											? '#fff1f0'
											: '#f0fdf4',
									color:
										distance?.gps_status?.toLowerCase() === 'offline'
											? '#ff4d4f'
											: '#16a34a',
									fontSize: '12px',
								}}
							>
								{distance?.gps_status}
							</Text>
						</Col>
					</Row>
					<Row justify="space-between" align="middle" style={{ marginTop: '16px' }}>
						<Col>
							<Text type="secondary" style={{ fontSize: '14px', color: '#64748b' }}>
								Last Location Ping
							</Text>
						</Col>
						<Col>
							<Text strong style={{ fontSize: '15px', color: '#1e293b' }}>
								{formatLastPingTime(distance?.last_location_ping)}
							</Text>
						</Col>
					</Row>
				</div>

				<div
					style={{
						backgroundColor: '#f8f9fa',
						borderRadius: '8px',
						padding: '20px 24px',
						borderLeft: '4px solid #f59e0b',
						marginBottom: '20px',
					}}
				>
					<Text
						strong
						style={{ fontSize: '12px', color: '#f59e0b', letterSpacing: '0.8px' }}
					>
						ODOMETER READINGS
					</Text>
					<Row justify="space-between" align="middle" style={{ marginTop: '16px' }}>
						<Col>
							<Text type="secondary" style={{ fontSize: '14px', color: '#64748b' }}>
								Start KM
							</Text>
						</Col>
						<Col>
							<Text strong style={{ fontSize: '15px', color: '#1e293b' }}>
								{formatKm(distance?.start_km)}
							</Text>
						</Col>
					</Row>
					<Row justify="space-between" align="middle" style={{ marginTop: '16px' }}>
						<Col>
							<Text type="secondary" style={{ fontSize: '14px', color: '#64748b' }}>
								End KM
							</Text>
						</Col>
						<Col>
							<Text strong style={{ fontSize: '15px', color: '#1e293b' }}>
								{formatKm(distance?.end_km)}
							</Text>
						</Col>
					</Row>
				</div>

				<div
					style={{
						backgroundColor: '#f8f9fa',
						borderRadius: '8px',
						padding: '20px 24px',
						border: '1px solid #f1f5f9',
					}}
				>
					<Row justify="space-between" align="middle" style={{ marginBottom: '12px' }}>
						<Col>
							<Text strong style={{ fontSize: '14px', color: '#1e293b' }}>
								Trip Progress
							</Text>
						</Col>
						<Col>
							<Text strong style={{ color: '#0ea5e9', fontSize: '15px' }}>
								{progress ? Math.floor(progress) : 0}%
							</Text>
						</Col>
					</Row>
					<Progress
						percent={progress ? Math.floor(progress) : 0}
						showInfo={false}
						strokeColor="#0ea5e9"
						trailColor="#e2e8f0"
						strokeWidth={10}
					/>
					<div style={{ textAlign: 'center', marginTop: '16px' }}>
						<Text type="secondary" style={{ fontSize: '13px', color: '#94a3b8' }}>
							{distance?.schedule_status}
						</Text>
					</div>
				</div>
			</div>
		</Card>
	);
}

DistanceMetricsCard.propTypes = {
	distance: PropTypes.object,
	progress: PropTypes.number,
};

export default DistanceMetricsCard;
